import { studyQueryKeys } from '@/config/queryKeys';
import { useDateFormatter } from '@/hooks';
import { createAgGridDatasource } from '@/lib/createAgGridDatasource';
import type { PaginatedStudiesResponse, StudyPublicGetMany } from '@/types';
import { Table } from '@nautilusbio/nb-design-system/Table';
import type { ColDef } from 'ag-grid-enterprise';
import type { AgGridReact } from 'ag-grid-react';
import clsx from 'clsx';
import React, { type FC, type RefObject, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import tableStyles from '../../../styles/components/table.module.css';
import globalStyles from '../../../styles/index.module.css';
import styles from '../styles.module.css';
import { createSetFilterParams } from '../utils/sharedColumnDefs';

import AvatarCellRenderer from './AvatarCellRenderer';
import StudyContactCell from './StudyContactCell';
import StudyNameCell from './StudyNameCell';

const localStorageKey = 'studies-agGridSortState';

export type StudiesTableProps = {
  gridRef: RefObject<AgGridReact<StudyPublicGetMany> | null>;
  selectedOrg: string;
};

const TableData: FC<StudiesTableProps> = ({ gridRef, selectedOrg }) => {
  const { t } = useTranslation('general');
  const formatDate = useDateFormatter();

  // Column Definitions: Defines & controls grid columns.
  // Using useMemo to react to formatDate changes (user date format preference)
  const colDefs = useMemo<ColDef<StudyPublicGetMany>[]>(
    () => [
      // id (hidden)
      { field: 'id', headerName: 'ID', hide: true, sortable: true, filter: 'agTextColumnFilter' },
      // name (with button)
      {
        cellClass: styles.tableCell,
        cellRenderer: StudyNameCell,
        field: 'name',
        headerName: 'Name',
        sortable: true,
        filter: 'agSetColumnFilter',
        filterParams: createSetFilterParams('name'),
      },

      // Study Contact composite column
      /**
       * Study Contact: Shows display name if present (with email in popover & copy),
       * else direct email (copyable), else '—'.
       * Handles null, safe checks. See StudyContactCell for encapsulated logic.
       */
      {
        cellClass: styles.tableCell,
        field: 'contact',
        headerName: 'Contact',
        hide: true,
        filter: false,
        maxWidth: 250,
        cellRenderer: (params: { data: StudyPublicGetMany; value: StudyPublicGetMany['contact'] }) => (
          <StudyContactCell id={params.data.id} value={params.value} />
        ),
      },

      // description
      {
        cellClass: styles.tableCell,
        field: 'description',
        headerName: 'Description',
        filter: false,
        maxWidth: 400,
        sortable: true,
      },

      // Organization and numeric/stat columns (all hidden by default)
      {
        field: 'org_name',
        headerName: 'Organization',
        maxWidth: 120,
        sortable: true,
        filter: 'agTextColumnFilter',
        valueFormatter: ({ value }) => (value && typeof value === 'string' ? value : '—'),
        hide: true,
      },

      // Numeric/stat columns (all hidden by default)
      {
        field: 'num_samples',
        headerName: 'Samples',
        maxWidth: 120,
        sortable: true,
        filter: 'agNumberColumnFilter',
        valueFormatter: ({ value }) => (typeof value === 'number' ? String(value) : '—'),
        hide: true,
      },
      {
        field: 'num_samples_passed_qc',
        headerName: 'QC-Passed Samples',
        hide: true,
        maxWidth: 130,
        sortable: true,
        filter: 'agNumberColumnFilter',
        valueFormatter: ({ value }) => (typeof value === 'number' ? String(value) : '—'),
      },
      {
        field: 'num_instrument_runs',
        headerName: 'Instrument Runs',
        hide: true,
        maxWidth: 130,
        sortable: true,
        filter: 'agNumberColumnFilter',
        valueFormatter: ({ value }) => (typeof value === 'number' ? String(value) : '—'),
      },
      {
        field: 'num_proteins',
        headerName: 'Proteins',
        hide: true,
        maxWidth: 120,
        sortable: true,
        filter: 'agNumberColumnFilter',
        valueFormatter: ({ value }) => (typeof value === 'number' ? String(value) : '—'),
      },
      {
        field: 'num_proteoform_groups',
        headerName: 'Proteoform Groups',
        hide: true,
        maxWidth: 150,
        sortable: true,
        filter: 'agNumberColumnFilter',
        valueFormatter: ({ value }) => (typeof value === 'number' ? String(value) : '—'),
      },

      // created_at
      {
        cellClass: styles.tableCell,
        field: 'created_at',
        headerName: t('created_at'),
        filter: 'agDateColumnFilter',
        filterParams: {
          filterOptions: ['equals', 'notEqual', 'lessThan', 'greaterThan', 'inRange', 'blank', 'notBlank'],
          suppressAndOrCondition: false,
          buttons: ['reset', 'apply'],
        },
        hide: true,
        maxWidth: 210,
        sortable: true,
        valueFormatter: ({ value }) => formatDate(value),
      },
      // updated_at
      {
        cellClass: styles.tableCell,
        field: 'updated_at',
        headerName: 'Updated At',
        filter: 'agDateColumnFilter',
        hide: true,
        sortable: true,
        valueFormatter: ({ value }) => formatDate(value),
      },
      // updated_by_full_name
      {
        cellClass: tableStyles.tableCellCenter,
        cellRenderer: AvatarCellRenderer,
        field: 'updated_by_full_name',
        headerName: 'Updated By',
        hide: true,
        sortable: true,
        filter: 'agSetColumnFilter',
        filterParams: createSetFilterParams('updated_by_full_name'),
      },
    ],
    [formatDate, t],
  );

  const tableContainerClasses = useMemo(() => ({ className: clsx(styles.table, globalStyles.scrollbars) }), []);

  // AG Grid server-side datasource using shared factory, backed by TanStack Query
  const datasource = useMemo(
    () =>
      createAgGridDatasource<StudyPublicGetMany, PaginatedStudiesResponse>(
        studyQueryKeys.endpoints.listSearch(selectedOrg),
      ),
    [selectedOrg],
  );

  useEffect(() => {
    if (gridRef.current?.api) {
      gridRef.current.api.setGridOption('serverSideDatasource', datasource);
    }
  }, [datasource, gridRef]);

  return (
    <Table
      columnDefs={colDefs}
      container={tableContainerClasses}
      domLayout="normal"
      onGridReady={e => {
        const savedColumnStateRaw = localStorage.getItem(localStorageKey);
        let savedColumnState = null;

        if (savedColumnStateRaw) {
          try {
            savedColumnState = JSON.parse(savedColumnStateRaw);
          } catch (err) {
            console.error('Failed to parse saved column state:', err);
            savedColumnState = null;
          }
        }

        if (savedColumnState) {
          e.api.applyColumnState({ state: savedColumnState });
        }

        e.api.setGridOption('serverSideDatasource', datasource);
      }}
      onSortChanged={e => {
        const columnState = e.api.getColumnState();
        localStorage.setItem(localStorageKey, JSON.stringify(columnState));
      }}
      pagination
      paginationAutoPageSize
      tableId="studies-table"
      enableResponsiveResize={true}
      resizeDebounceMs={150}
      ref={gridRef}
      rowModelType="serverSide"
      zebraStriping
    />
  );
};

export default TableData;
